/* eslint-disable max-len */
import formLabels_de from './de/labels.json'
import formNotes_de from './de/notes.json'
import tableHeaders_de from './de/tableHeaders.json'

import enumTranslations_de from './de/enum.json'

const translationPrefixes = {
  labels: 'form_label_',
  notes: 'form_note_',
  tableHeaders: 'tabTitle_',
  stateHistoryText: 'stateHistoryText_',
  transitionFiredText: 'transitionFiredText_',
  btnTxt: 'btnTxt_',
} as const

export function addPrefix<TTyp extends keyof typeof translationPrefixes, TKey extends string>(
  typ: TTyp,
  key: TKey
): `${(typeof translationPrefixes)[TTyp]}${TKey}` {
  return `${translationPrefixes[typ]}${key}`
}

export function addKeyPrefix<
  TTyp extends keyof typeof translationPrefixes,
  TKeys extends string,
  TValues extends string,
>(typ: TTyp, obj: Record<TKeys, TValues>) {
  const entries = Object.entries(obj)
  const prefixedEntries = entries.map(([key, value]) => [addPrefix(typ, key), value])

  const prefixedObj = Object.fromEntries(prefixedEntries) as {
    [key in TKeys as ReturnType<typeof addPrefix<TTyp, key>>]: (typeof obj)[key]
  }

  return prefixedObj
}

export type CanonicalFormLabels = keyof typeof formLabels_de

// TODO: this might not be save, as typescript is TS is not strictly typed
//       But they are loaded from json, so it *should* be fine I guess
export const canonicalFormLabels = Object.keys(formLabels_de) as CanonicalFormLabels[]

export type FormLabelKeys = ReturnType<typeof addPrefix<'labels', CanonicalFormLabels>>

export function asFormLabel(candidate: string): CanonicalFormLabels | undefined {
  if ((canonicalFormLabels as string[]).includes(candidate)) {
    return candidate as CanonicalFormLabels
  }
  return undefined
}

export type OnlyValideLabels<T> = T extends CanonicalFormLabels ? T : never

export type CanonicalFormNotes = keyof typeof formNotes_de
export const canonicalFormNotes = Object.keys(formNotes_de) as CanonicalFormNotes[]
export type FormNotesKeys = ReturnType<typeof addPrefix<'notes', CanonicalFormNotes>>


export type CanonicalTableHeaders = keyof typeof tableHeaders_de
export const canonicalTableHeaders = Object.keys(tableHeaders_de) as CanonicalTableHeaders[]
export type TableHeadersKeys = ReturnType<typeof addPrefix<'notes', CanonicalTableHeaders>>

export type CanonicalEnumTranslations = keyof typeof enumTranslations_de
export const canonicalEnumTranslations = Object.keys(enumTranslations_de) as CanonicalEnumTranslations[]
export type EnumTranslationsKeys = ReturnType<typeof addPrefix<'notes', CanonicalEnumTranslations>>

export function BuildTranslationMap(
  formLabels: Record<CanonicalFormLabels, string>,
  formNotes: Record<CanonicalFormNotes, string>,
  tableHeaders: Record<CanonicalTableHeaders, string>,
  enumTranslations: Record<CanonicalEnumTranslations, string>,
) {
  return {
    ...addKeyPrefix('labels', formLabels),
    ...addKeyPrefix('notes', formNotes),
    ...addKeyPrefix('tableHeaders', tableHeaders),
    ...enumTranslations,

    ...addKeyPrefix('stateHistoryText', {
      workOn_Vertrieb2PE: "Das Projekt wird für die Projektinitiierung bearbeitet.",
      workOn_PE2Vertrieb: "Das Projekt wird für das Standortkonzept bearbeitet.",
      workOn_Vertrieb2ENG: "Das Projekt wird für die Übergabe von Vertrieb an ENG bearbeitet.",
      workOn_ENG2Vertrieb: "Das Projekt wird für die Umsetzungsplanung bearbeitet.",
      workOn_CompletionOfPlanning: "Das Projekt wird für den Planungsabschluss bearbeitet.",
      workOn_CompletionOfPlanning2Vertrieb: "Das Projekt wird für die Prüfung des Planungsabschlusses durch Vertrieb vorbereitet.",
      workOn_Vertrieb2Projektleitung: "Das Projekt wird für die Übergabe von Vertrieb an Projektleitung bearbeitet.",
      workOn_Projektleitung2SuW: "Das Projekt wird für die Übergabe von Projektleitung an Team Betrieb bearbeitet.",
      workOn_SuW: "Das Projekt befindet sich in der finalen Abnahme durch Team Betrieb.",
      type_Elektifizierung_AC: "Es handelt sich um ein Elektifizierung AC Projekt.",
      type_Elektifizierung_DC: "Es handelt sich um ein Elektifizierung DC Projekt.",
      type_komplexe_DC_inclIBN: "Es handelt sich um ein Komplexe DC inkl. IBN (mit ENG) Projekt.",
      type_standart_DC_inclIBN: "Es handelt sich um ein Standardisierte DC inkl. IBN (ohne PEENG) Projekt.",
      type_auslieferung_DC: "Es handelt sich um ein Auslieferung ohne Konfig. Projekt.",
      type_ac_inc_config: "Es handelt sich um ein AC inc. Konfig Projekt.",
      preHandover_PE2Vertrieb: "Die Akte wird vor dem Standortkonzept überprüft.",
      preHandover_CompletionOfPlanning: "Die Akte wird vor dem Planungsabschluss überprüft.",
      preHandover_Projektleitung2SuW: "Die Akte wird vor der Übergabe von Projektleitung an Team Betrieb überprüft.",
      decision_StandortkonzeptOrUmsetzungsplanung: "Projektartenermittlung",
      decision_DCS_LIS_A: "Projektartenermittlung",
      decision_DCS_LIS_B: "Projektartenermittlung",
      decision_dienstleitungOrIbnKonfig: "Projektartenermittlung",
      decision_Standortkonzept: "Projektartenermittlung",
      decision_UmsetzungsplanungBeauftragt: "Möchte der Kunde mit der Umsetzungsplanung starten?",
      decision_UmsetzungBeauftragt: "Projektartenermittlung",
      decision_DL_verkauft: "Projektartenermittlung",
      handover_Vertrieb2PE: "Die Projektinitiierung wurde angefragt.",
      handover_PE2Vertrieb: "Das Standortkonzept wurde angefragt.",
      handover_Vertrieb2ENG: "Die Übergabe von Vertrieb an ENG wurde angefragt.",
      handover_ENG2Vertrieb: "Die Umsetzungsplanung wurde angefragt.",
      handover_CompletionOfPlanning: "Der Planungsabschluss wurde zur Übergabe freigegeben.",
      handover_CompletionOfPlanning2Vertrieb: "Der Planungsabschluss wurde zur Prüfung durch Vertrieb freigegeben.",
      handover_Vertrieb2Projektleitung: "Die Übergabe von Vertrieb an Projektleitung wurde angefragt.",
      handover_Projektleitung2SuW: "Die Übergabe von Projektleitung an Team Betrieb wurde angefragt.",
      terminated_preUmsetzungsplanung: "Da keine Umsetzungsplanung beauftragt wurde ist die Akte geschlossen.",
      terminated_preUmsetzung: "Da keine Umsetzung beauftragt wurde ist die Akte geschlossen.",
      terminated_inBetrieb: "Das Projekt ist in Betrieb.",
      //TODO: add new label for a new state, if required

      // updated process for projektleitung > suw > inBetrieb / inFremdbetrieb
      entry_Projektleitung2SuW: "Das Projekt wird für die Übergabe von Projektleitung an Team Betrieb vorbereitet.",
      decision_preliminaryHandover_Projektleitung2SuW: "Art der Übergabe an Team Betrieb.",
      workOn_directFinalHandover_Projektleitung2SuW: "Das Projekt wird für die finale Übergabe an Team Betrieb bearbeitet.",
      handover_directFinalHandover_Projektleitung2SuW: "Die finale Übergabe von der Projektleitung an Team Betrieb wurde angefragt.",
      workOn_finalHandover_Projektleitung2SuW: "Das Projekt wird für die finale Übergabe an Team Betrieb vorbereitet.",
      workOn_preliminaryHandover_Projektleitung2SuW: "Das Projekt wird für die vorläufige Übergabe an Team Betrieb bearbeitet.",
      handover_preliminaryHandover_Projektleitung2SuW: "Die vorläufige Übergabe von der Projektleitung an Team Betrieb wurde angefragt.",
      workOn_preliminaryToFinalHandover_Projektleitung2SuW: "Das Projekt wird für die finale Übergabe an Team Betrieb bearbeitet.",
      handover_preliminaryToFinalHandover_Projektleitung2SuW: "Die finale Übergabe von der Projektleitung an Team Betrieb wurde angefragt.",
      handover_terminated_inBetrieb: "Die finale Übergabe an Team Betrieb wurde angefragt.",
      workOn_Projektleitung2SuW_withoutContract: "Das Projekt wird für die Gewährleistungsübergabe vorbereitet.",
      handover_GewaehrleistungsUebergabe: "Die Gewährleistungsübergabe wurde angefragt.",
      workOn_Fremdbetrieb: "Das Projekt wird für die Übergabe an Fremdbetrieb vorbereitet.",
      handover_terminated_inFremdbetrieb: "Die Übergabe an Fremdbetrieb wurde angefragt.",
      terminated_inFremdbetrieb: "Das Projekt ist in Fremdbetrieb.",
    }),
    ...addKeyPrefix('transitionFiredText', {
      decision_standortkonzept_NEIN: 'Planung ist nicht Teil des Projekts.',
      decision_standortkonzept_JA: 'Planung ist Teil des Projekts.',
      decision_DCS_LIS_B_NEIN: 'DC LIS ist nicht Bestandteil.',
      decision_DCS_LIS_B_JA: 'DC LIS ist Bestandteil.',
      decision_dienstleitungOrIbnKonfig_JA: 'Es werden Elektro- oder Tiefbauarbeiten durch Connect organisiert.',
      decision_dienstleitungOrIbnKonfig_NEIN: 'Es wurde IBN oder Konfig verkauft.',
      decision_DCS_LIS_A_NEIN: 'DC LIS ist nicht Bestandteil.',
      decision_DCS_LIS_A_JA: 'DC LIS ist Bestandteil.',
      decision_DL_NEIN: 'Es wurde keine DL verkauft.',
      decision_DL_JA: 'Es wurde eine DL verkauft.',

      start_Elektifizierung_AC: 'Es handelt sich um ein Elektrifizierung AC Projekt.',
      start_Elektifizierung_DC: 'Es handelt sich um ein Elektrifizierung DC Projekt.',
      decision_Standortkonzept_JA: 'Es wurde ein Standortkonzept verkauft',
      decision_Standortkonzept_NEIN: 'Es wurde kein Standortkonzept verkauft',
      start_komplexe_DC_inclIBN: 'Es handelt sich um ein Komplexe DC inkl. IBN (mit ENG) Projekt.',
      start_type_standart_DC_inclIBN: 'Es handelt sich um ein Standardisierte DC inkl. IBN (ohne PEENG) Projekt.',
      start_type_auslieferung_DC: 'Es handelt sich um ein Auslieferung ohne Konfig. Projekt.',
      start_type_ac_inc_config: 'Es handelt sich um ein AC inc. Konfig Projekt.',
      migration_DC: 'Es handelt sich um ein Standardisierte DC inkl. IBN (ohne PEENG) Projekt.',
      migration_AC: 'Es handelt sich um ein AC inc. Konfig Projekt.',
      Vertrieb2PE_request: 'Die Projektinitiierung wurde angefragt.',
      Vertrieb2PE_approved: 'Projektinitiierung wurde angenommen.',
      Vertrieb2PE_rejected: 'Projektinitiierung wurde abgelehnt.',
      PE2Vertrieb_preRequest: 'Eine überprüfung des Standortkonzepts wurde angefragt.',
      PE2Vertrieb_request: 'Standortkonzept wurde zur Übergabe freigegeben.',
      PE2Vertrieb_preRequestRejected: 'Standortkonzept wurde zurückgewiesen.',
      PE2Vertrieb_approved: 'Standortkonzept wurde angenommen.',
      PE2Vertrieb_rejected: 'Standortkonzept wurde abgelehnt.',
      decision_UmsetungsplanungBeantragt_JA: 'Der Kunde möchte mit der Umsetzungsplanung starten.',
      decision_UmsetungsplanungBeantragt_NEIN: 'Der Kunde möchte nicht mit der Umsetzungsplanung starten.',
      decision_reactivate_UmsetungsplanungBeantragt: 'Die Entscheidung zur Umsetzungsplanung wurde reaktiviert.',
      Vertrieb2ENG_request: 'Die Übergabe von Vertrieb an ENG wurde angefragt.',
      Vertrieb2ENG_approved: 'Vertrieb an ENG wurde angenommen.',
      Vertrieb2ENG_rejected: 'Vertrieb an ENG wurde abgelehnt.',
      ENG2Vertrieb_request: 'Die Umsetzungsplanung wurde angefragt.',
      ENG2Vertrieb_rejected: 'Die Umsetzungsplanung wurde abgelehnt.',
      ENG2Vertrieb_approved: 'Die Umsetzungsplanung wurde angenommen.',
      CompletionOfPlanning_preRequest: 'Der Planungsabschluss wurde zur Prüfung angefragt.',
      CompletionOfPlanning_request: 'Planungsabschluss wurde zur Übergabe freigegeben.',
      CompletionOfPlanning_preRequestRejected: 'Planungsabschluss wurde zurückgewiesen.',
      CompletionOfPlanning_approved: 'Der Planungsabschluss wurde angenommen.',
      CompletionOfPlanning_rejected: 'Der Planungsabschluss wurde abgelehnt.',
      CompletionOfPlanning2Vertrieb_request: 'Der Planungsabschluss wurde zur Prüfung durch Vertrieb freigegeben.',
      CompletionOfPlanning2Vertrieb_approved: 'Der Planungsabschluss wurde durch Vertrieb angenommen.',
      CompletionOfPlanning2Vertrieb_rejected: 'Der Planungsabschluss wurde durch Vertrieb abgelehnt.',
      decision_UmsetungsBeantragt_JA: 'Die Umsetzung wurde beauftragt.',
      decision_UmsetungsBeantragt_NEIN: 'Es wurde keine Umsetzung beauftragt.',
      decision_reactivate_UmsetungsBeantragt: 'Das Projekt wurde reaktiviert.',
      Projektleitung2SuW_preRequest: 'Eine überprüfung von Projektleitung an Team Betrieb wurde angefragt.',
      Vertrieb2Projektleitung_request: 'Die Übergabe von Vertrieb an Projektleitung wurde angefragt.',
      Vertrieb2Projektleitung_approved: 'Vertrieb an Projektleitung wurde angenommen.',
      Vertrieb2Projektleitung_rejected: 'Vertrieb an Projektleitung wurde abgelehnt.',
      Projektleitung2SuW_request: 'Die Übergabe von Projektleitung an Team Betrieb wurde angefragt.',
      Projektleitung2SuW_request_old: 'Die Übergabe von Projektleitung an Team Betrieb wurde angefragt.',
      Projektleitung2SuW_rejected: 'Projektleitung an Team Betrieb wurde abgelehnt.',
      Projektleitung2SuW_approved: 'Projektleitung an Team Betrieb wurde angenommen.',
      SuW_betrieb: 'Das Projekt wurde durch Team Betrieb in betrieb genommen.',
      SuW_betrieb_revoke: 'Das Projekt wurde durch Team Betrieb wieder aus dem Betrieb genommen.',
      returnFrom_SuW_TO_Vertrieb2PE: 'Rückgabe zur Projektinitiierung.',
      returnFrom_SuW_TO_PE2Vertrieb: 'Rückgabe zu Standortkonzept.',
      returnFrom_SuW_TO_Vertrieb2ENG: 'Rückgabe zu Vertrieb an ENG.',
      returnFrom_SuW_TO_ENG2Vertrieb: 'Rückgabe zur Umsetzungsplanung.',
      returnFrom_SuW_TO_Vertrieb2Projektleitung: 'Rückgabe zu Vertrieb an Projektleitung.',
      returnFrom_SuW_TO_Projektleitung2SuW: 'Rückgabe zu Projektleitung an Team Betrieb.',
      returnFrom_Projektleitung2SuW_TO_Vertrieb2PE: 'Rückgabe zur Projektinitiierung.',
      returnFrom_Projektleitung2SuW_TO_PE2Vertrieb: 'Rückgabe zu Standortkonzept.',
      returnFrom_Projektleitung2SuW_TO_Vertrieb2ENG: 'Rückgabe zu Vertrieb an ENG.',
      returnFrom_Projektleitung2SuW_TO_ENG2Vertrieb: 'Rückgabe zur Umsetzungsplanung.',
      returnFrom_Projektleitung2SuW_TO_Vertrieb2Projektleitung: 'Rückgabe zu Vertrieb an Projektleitung.',
      returnFrom_CompletionOfPlanning_TO_ENG2Vertrieb: 'Rückgabe zur Umsetzungsplanung.',
      returnFrom_Vertrieb2Projektleitung_TO_CompletionOfPlanning: 'Rückgabe zum Planungsabschluss.',
      returnFrom_Vertrieb2Projektleitung_TO_Vertrieb2PE: 'Rückgabe zur Projektinitiierung.',
      returnFrom_Vertrieb2Projektleitung_TO_PE2Vertrieb: 'Rückgabe zu Standortkonzept.',
      returnFrom_Vertrieb2Projektleitung_TO_Vertrieb2ENG: 'Rückgabe zu Vertrieb an ENG.',
      returnFrom_Vertrieb2Projektleitung_TO_ENG2Vertrieb: 'Rückgabe zur Umsetzungsplanung.',
      returnFrom_ENG2Vertrieb_TO_Vertrieb2PE: 'Rückgabe zur Projektinitiierung.',
      returnFrom_ENG2Vertrieb_TO_PE2Vertrieb: 'Rückgabe zu Standortkonzept.',
      returnFrom_ENG2Vertrieb_TO_Vertrieb2ENG: 'Rückgabe zu Vertrieb an ENG.',
      returnFrom_Vertrieb2ENG_TO_Vertrieb2PE: 'Rückgabe zur Projektinitiierung.',
      returnFrom_Vertrieb2ENG_TO_PE2Vertrieb: 'Rückgabe zu Standortkonzept.',
      returnFrom_PE2Vertrieb_TO_Vertrieb2PE: 'Rückgabe zur Projektinitiierung.',
      returnFrom_GewaehrleistungsUebergabe_TO_Projektleitung2SuW: 'Rückgabe zu Vertrieb an Projektleitung.',
      returnFrom_preliminaryHandover_Projektleitung2SuW_TO_Projektleitung2SuW: 'Rückgabe zu Vertrieb an Projektleitung.',
      returnFrom_directFinalHandover_Projektleitung2SuW_TO_Projektleitung2SuW: 'Rückgabe zu Projektleitung an Team Betrieb.',
      returnFrom_preliminaryToFinalHandover_Projektleitung2SuW_TO_Projektleitung2SuW: 'Rückgabe zu Projektleitung an Team Betrieb.',
      returnFrom_finalHandover_Projektleitung2SuW_TO_Projektleitung2SuW: 'Rückgabe zu Projektleitung an Team Betrieb.',
      returnFrom_preliminaryToFinalHandover_Projektleitung2SuW_TO_preliminaryHandover_Projektleitung2SuW: 'Rückgabe zur vorläufigen Übergabe an Team Betrieb.',
      returnFrom_finalHandover_Projektleitung2SuW_TO_preliminaryHandover_Projektleitung2SuW: 'Rückgabe zur vorläufigen Übergabe an Team Betrieb.',

      decision_preliminaryHandover_Projektleitung2SuW_NEIN: "Es soll eine finale Übergabe an Team Betrieb beantragt werden.",
      decision_preliminaryHandover_Projektleitung2SuW_JA: "Es soll eine vorläufige Übergabe an Team Betrieb beantragt werden.",
      entry_Projektleitung2SuW_withServiceContract: "Es handelt sich um ein Projekt mit Servicevertrag.",
      entry_Projektleitung2SuW_withoutServiceContract: "Es handelt sich um ein Projekt ohne Servicevertrag.",
      directFinalHandover_Projektleitung2SuW_request: 'Die finale Übergabe an Team Betrieb wurde angefragt.',
      directFinalHandover_Projektleitung2SuW_approved: 'Die finale Übergabe an Team Betrieb wurde angenommen.',
      directFinalHandover_Projektleitung2SuW_rejected: 'Die finale Übergabe an Team Betrieb wurde abgelehnt.',
      preliminaryHandover_Projektleitung2SuW_request: 'Die vorläufige Übergabe an Team Betrieb wurde angefragt.',
      preliminaryHandover_Projektleitung2SuW_approved: 'Die vorläufige Übergabe an Team Betrieb wurde angenommen.',
      preliminaryHandover_Projektleitung2SuW_rejected: 'Die vorläufige Übergabe an Team Betrieb wurde abgelehnt.',
      preliminaryToFinalHandover_Projektleitung2SuW_request: 'Die finale Übergabe an Team Betrieb wurde angefragt.',
      preliminaryToFinalHandover_Projektleitung2SuW_approved: 'Die finale Übergabe an Team Betrieb wurde angenommen.',
      preliminaryToFinalHandover_Projektleitung2SuW_rejected: 'Die finale Übergabe an Team Betrieb wurde abgelehnt.',
      SuWBetrieb_request: 'Die finale Übergabe an Team Betrieb wurde angefragt.',
      SuWBetrieb_approved: 'Die finale Übergabe an Team Betrieb wurde angenommen.',
      SuWBetrieb_rejected: 'Die finale Übergabe an Team Betrieb wurde abgelehnt.',
      GewaehrleistungsUebergabe_request: 'Die Gewährleistungsübergabe wurde angefragt.',
      GewaehrleistungsUebergabe_approved: 'Die Gewährleistungsübergabe wurde angenommen.',
      GewaehrleistungsUebergabe_rejected: 'Die Gewährleistungsübergabe wurde abgelehnt.',
      Fremdbetrieb_request: 'Die Übergabe an Fremdbetrieb wurde angefragt.',
      Fremdbetrieb_request_old: 'Das Projekt wurde in Fremdbetrieb gesetzt.',
      Fremdbetrieb_approved: 'Die Übergabe an Fremdbetrieb wurde angenommen.',
      Fremdbetrieb_rejected: 'Die Übergabe an Fremdbetrieb wurde abgelehnt.',
    }),

    btnTxt_decision_standortkonzept_NEIN: 'Planung ist nicht Teil des Projekts',
    btnTxt_decision_standortkonzept_JA: 'Planung ist Teil des Projekts',
    btnTxt_decision_DCS_LIS_B_NEIN: 'DC LIS ist nicht Bestandteil',
    btnTxt_decision_DCS_LIS_B_JA: 'DC LIS ist Bestandteil',
    btnTxt_decision_dienstleitungOrIbnKonfig_JA: 'Es werden Elektro- oder Tiefbauarbeiten durch Connect organisiert.',
    btnTxt_decision_dienstleitungOrIbnKonfig_NEIN: 'Es werden keine Elektro- oder Tiefbauarbeiten organisiert.',
    btnTxt_decision_DCS_LIS_A_NEIN: 'DC LIS ist nicht Bestandteil',
    btnTxt_decision_DCS_LIS_A_JA: 'DC LIS ist Bestandteil',
    btnTxt_decision_DL_NEIN: 'Es wurde keine DL verkauft',
    btnTxt_decision_DL_JA: 'Es wurde eine DL verkauft',
    btnTxt_decision_Standortkonzept_JA: 'Es wurde ein Standortkonzept verkauft',
    btnTxt_decision_Standortkonzept_NEIN: 'Es wurde kein Standortkonzept verkauft',
    btnTxt_Vertrieb2PE_request: 'Übergabe beantragen',
    btnTxt_Vertrieb2PE_approved: 'Übergabe annehmen',
    btnTxt_Vertrieb2PE_rejected: 'Übergabe ablehnen',
    btnTxt_PE2Vertrieb_preRequest: 'Überprüfung beantragen',
    btnTxt_PE2Vertrieb_request: 'Übergabe beantragen',
    btnTxt_PE2Vertrieb_preRequestRejected: 'Überprüfung zurückweisen',
    btnTxt_PE2Vertrieb_approved: 'Übernahme annehmen',
    btnTxt_PE2Vertrieb_rejected: 'Übernahme ablehnen',
    btnTxt_decision_UmsetungsplanungBeantragt_JA: 'Umsetzungsplanung jetzt starten. Kunde nutzt Sonderkündigungsrecht nicht.',
    btnTxt_decision_UmsetungsplanungBeantragt_NEIN: 'Projekt abgebrochen. Kunde nutzt Sonderkündigungsrecht.',
    btnTxt_decision_reactivate_UmsetungsplanungBeantragt: 'Projekt reaktivieren',
    btnTxt_Vertrieb2ENG_request: 'Übergabe beantragen',
    btnTxt_Vertrieb2ENG_approved: 'Übergabe annehmen',
    btnTxt_Vertrieb2ENG_rejected: 'Übergabe ablehnen',
    btnTxt_ENG2Vertrieb_request: 'Umsetzungsplanung abschließen',
    btnTxt_ENG2Vertrieb_rejected: 'Übergabe ablehnen',
    btnTxt_ENG2Vertrieb_approved: 'Übergabe annehmen',
    btnTxt_CompletionOfPlanning_preRequest: 'Prüfung beantragen',
    btnTxt_CompletionOfPlanning_request: 'Übergabe beantragen',
    btnTxt_CompletionOfPlanning_preRequestRejected: 'Überprüfung zurückweisen',
    btnTxt_CompletionOfPlanning_approved: 'Übergabe annehmen',
    btnTxt_CompletionOfPlanning_rejected: 'Übergabe ablehnen',
    btnTxt_CompletionOfPlanning2Vertrieb_request: 'Prüfung durch Vertrieb beantragen',
    btnTxt_CompletionOfPlanning2Vertrieb_approved: 'Prüfung durch Vertrieb annehmen',
    btnTxt_CompletionOfPlanning2Vertrieb_rejected: 'Prüfung durch Vertrieb ablehnen',
    btnTxt_decision_UmsetungsBeantragt_JA: 'Die Umsetzung wurde beauftragt',
    btnTxt_decision_UmsetungsBeantragt_NEIN: 'Es wurde keine Umsetzung beauftragt',
    btnTxt_decision_reactivate_UmsetungsBeantragt: 'Projekt reaktivieren',
    btnTxt_Vertrieb2Projektleitung_request: 'Übergabe beantragen',
    btnTxt_Vertrieb2Projektleitung_approved: 'Übergabe annehmen',
    btnTxt_Vertrieb2Projektleitung_rejected: 'Übergabe ablehnen',
    btnTxt_Projektleitung2SuW_preRequest: 'Überprüfung beantragen',
    btnTxt_Projektleitung2SuW_preRequestRejected: 'Überprüfung zurückweisen',
    btnTxt_Projektleitung2SuW_request: 'Übergabe beantragen',
    btnTxt_Projektleitung2SuW_request_old: 'Übergabe beantragen',
    btnTxt_Projektleitung2SuW_rejected: 'Übergabe ablehnen',
    btnTxt_Projektleitung2SuW_approved: 'Übergabe annehmen',
    btnTxt_SuW_betrieb: 'Projekt in Betrieb stellen',
    btnTxt_SuW_betrieb_revoke: 'Projekt außer Betrieb stellen',
    btnTxt_SuWBetrieb: 'Finale Übergabe beantragen',
    btnTxt_SuWBetrieb_request: 'Finale Übergabe beantragen',
    btnTxt_SuWBetrieb_approved: 'Finale Übergabe an Team Betrieb annehmen',
    btnTxt_SuWBetrieb_rejected: 'Finale Übergabe an Team Betrieb ablehnen',

    // updated process for projektleitung > suw > inBetrieb / inFremdbetrieb
    btnTxt_decision_preliminaryHandover_Projektleitung2SuW: 'Weiter zur Übergabe',
    btnTxt_decision_preliminaryHandover_Projektleitung2SuW_NEIN: 'Finale Übergabe an Team Betrieb',
    btnTxt_decision_preliminaryHandover_Projektleitung2SuW_JA: 'Vorläufige Übergabe an Team Betrieb',
    btnTxt_directFinalHandover_Projektleitung2SuW_request: 'Finale Übergabe an Team Betrieb beantragen',
    btnTxt_directFinalHandover_Projektleitung2SuW_approved: 'Finale Übergabe an Team Betrieb annehmen',
    btnTxt_directFinalHandover_Projektleitung2SuW_rejected: 'Finale Übergabe an Team Betrieb ablehnen',
    btnTxt_preliminaryHandover_Projektleitung2SuW_request: 'Vorläufige Übergabe an Team Betrieb beantragen',
    btnTxt_preliminaryHandover_Projektleitung2SuW_approved: 'Vorläufige Übergabe an Team Betrieb annehmen',
    btnTxt_preliminaryHandover_Projektleitung2SuW_rejected: 'Vorläufige Übergabe an Team Betrieb ablehnen',
    btnTxt_preliminaryToFinalHandover_Projektleitung2SuW_request: 'Finale Übergabe an Team Betrieb beantragen',
    btnTxt_preliminaryToFinalHandover_Projektleitung2SuW_approved: 'Finale Übergabe an Team Betrieb annehmen',
    btnTxt_preliminaryToFinalHandover_Projektleitung2SuW_rejected: 'Finale Übergabe an Team Betrieb ablehnen',
    btnTxt_finalHandover_Projektleitung2SuW_request: 'Finale Übergabe beantragen',
    btnTxt_finalHandover_Projektleitung2SuW_approved: 'Finale Übergabe annehmen',
    btnTxt_finalHandover_Projektleitung2SuW_rejected: 'Finale Übergabe ablehnen',
    btnTxt_GewaehrleistungsUebergabe_request: 'Gewährleistungsübergabe beantragen',
    btnTxt_GewaehrleistungsUebergabe_approved: 'Gewährleistungsübergabe annehmen',
    btnTxt_GewaehrleistungsUebergabe_rejected: 'Gewährleistungsübergabe ablehnen',
    btnTxt_Fremdbetrieb_request: 'Übergabe an Fremdbetrieb beantragen',
    btnTxt_Fremdbetrieb_request_old: 'Projekt in Fremdbetrieb setzen',
    btnTxt_Fremdbetrieb_approved: 'Übergabe an Fremdbetrieb annehmen',
    btnTxt_Fremdbetrieb_rejected: 'Übergabe an Fremdbetrieb ablehnen',

    btnTxt_returnFrom_Vertrieb2PE: 'Zurückgeben',
    btnTxt_returnFrom_PE2Vertrieb: 'Zurückgeben',
    btnTxt_returnFrom_Vertrieb2ENG: 'Zurückgeben',
    btnTxt_returnFrom_ENG2Vertrieb: 'Zurückgeben',
    btnTxt_returnFrom_CompletionOfPlanning: 'Zurückgeben',
    btnTxt_returnFrom_Vertrieb2Projektleitung: 'Zurückgeben',
    btnTxt_returnFrom_Projektleitung2SuW: 'Zurückgeben',
    btnTxt_returnFrom_preliminaryHandover_Projektleitung2SuW: 'Zurückgeben',
    btnTxt_returnFrom_finalHandover_Projektleitung2SuW: 'Zurückgeben',
    btnTxt_returnFrom_SuW2Running: 'Zurückgeben',
    btnTxt_returnFrom: 'Zurückgeben',

    missingRights_decision_standortkonzept_NEIN: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_standortkonzept_JA: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_DCS_LIS_B_NEIN: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_DCS_LIS_B_JA: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_dienstleitungOrIbnKonfig_JA: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_dienstleitungOrIbnKonfig_NEIN: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_DCS_LIS_A_NEIN: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_DCS_LIS_A_JA: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_DL_NEIN: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_DL_JA: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_Standortkonzept_JA: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_Standortkonzept_NEIN: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_UmsetungsplanungBeantragt_JA: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_UmsetungsplanungBeantragt_NEIN: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_UmsetungsBeantragt_JA: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_reactivate_UmsetungsplanungBeantragt: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_UmsetungsBeantragt_NEIN: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_reactivate_UmsetungsBeantragt: 'Fehlende Rechte',
    missingRights_Vertrieb2PE_request: 'Fehlende Rechte für Anfrage',
    missingRights_Vertrieb2PE_approved: 'Fehlende Rechte zum annehmen',
    missingRights_Vertrieb2PE_rejected: 'Fehlende Rechte zum ablehnen',
    missingRights_PE2Vertrieb_preRequest: 'Fehlende Rechte zur Überprüfungsanfrage',
    missingRights_PE2Vertrieb_request: 'Fehlende Rechte für Anfrage',
    missingRights_PE2Vertrieb_preRequestRejected: 'Fehlende Rechte zum Zurückweisen',
    missingRights_PE2Vertrieb_approved: 'Fehlende Rechte zum annehmen',
    missingRights_PE2Vertrieb_rejected: 'Fehlende Rechte zum ablehnen',
    missingRights_Vertrieb2ENG_request: 'Fehlende Rechte für Anfrage',
    missingRights_Vertrieb2ENG_approved: 'Fehlende Rechte zum annehmen',
    missingRights_Vertrieb2ENG_rejected: 'Fehlende Rechte zum ablehnen',
    missingRights_ENG2Vertrieb_request: 'Fehlende Rechte zum Abschließen',
    missingRights_ENG2Vertrieb_approved: 'Fehlende Rechte zum annehmen',
    missingRights_ENG2Vertrieb_rejected: 'Fehlende Rechte zum ablehnen',
    missingRights_CompletionOfPlanning_preRequest: 'Fehlende Rechte für Anfrage',
    missingRights_CompletionOfPlanning_preRequestRejected: 'Fehlende Rechte zum ablehnen',
    missingRights_CompletionOfPlanning_request: 'Fehlende Rechte für Anfrage',
    missingRights_CompletionOfPlanning_approved: 'Fehlende Rechte zum annehmen',
    missingRights_CompletionOfPlanning_rejected: 'Fehlende Rechte zum ablehnen',
    missingRights_CompletionOfPlanning2Vertrieb_request: 'Fehlende Rechte für Anfrage',
    missingRights_CompletionOfPlanning2Vertrieb_approved: 'Fehlende Rechte zum annehmen',
    missingRights_CompletionOfPlanning2Vertrieb_rejected: 'Fehlende Rechte zum ablehnen',
    missingRights_Vertrieb2Projektleitung_request: 'Fehlende Rechte für Anfrage',
    missingRights_Vertrieb2Projektleitung_approved: 'Fehlende Rechte zum annehmen',
    missingRights_Vertrieb2Projektleitung_rejected: 'Fehlende Rechte zum ablehnen',
    missingRights_Projektleitung2SuW_preRequest: 'Fehlende Rechte',
    missingRights_Projektleitung2SuW_request: 'Fehlende Rechte für Anfrage',
    missingRights_Projektleitung2SuW_request_old: 'Fehlende Rechte für Anfrage',
    missingRights_Projektleitung2SuW_preRequestRejected: 'Fehlende Rechte',
    missingRights_Projektleitung2SuW_approved: 'Fehlende Rechte zum annehmen',
    missingRights_Projektleitung2SuW_rejected: 'Fehlende Rechte zum ablehnen',
    missingRights_SuW_betrieb: 'Fehlende Rechte zur finalen Übergabe an Team Betrieb',
    missingRights_SuW_betrieb_revoke: 'Fehlende Rechte zur Ausbetriebnahme',
    missingRights_SuW_archive: 'Fehlende Rechte zur Archivierung',
    missingRights_SuW_unarchive: 'Fehlende Rechte um Archivierung zurückzuziehen',
    missingRights_returnFrom_Vertrieb2PE: 'Fehlende Rechte für Rückgabe',
    missingRights_returnFrom_PE2Vertrieb: 'Fehlende Rechte für Rückgabe',
    missingRights_returnFrom_Vertrieb2ENG: 'Fehlende Rechte für Rückgabe',
    missingRights_returnFrom_ENG2Vertrieb: 'Fehlende Rechte für Rückgabe',
    missingRights_returnFrom_CompletionOfPlanning: 'Fehlende Rechte für Rückgabe',
    missingRights_returnFrom_Vertrieb2Projektleitung: 'Fehlende Rechte für Rückgabe',
    missingRights_returnFrom_Projektleitung2SuW: 'Fehlende Rechte für Rückgabe',
    missingRights_returnFrom_preliminaryHandover_Projektleitung2SuW: 'Fehlende Rechte für Rückgabe',
    missingRights_returnFrom_finalHandover_Projektleitung2SuW: 'Fehlende Rechte für Rückgabe',
    missingRights_returnFrom_SuW2Running: 'Fehlende Rechte für Rückgabe',

    missingRights_decision_preliminaryHandover_Projektleitung2SuW_JA: 'Fehlende Rechte für Endscheidung',
    missingRights_decision_preliminaryHandover_Projektleitung2SuW_NEIN: 'Fehlende Rechte für Endscheidung',
    missingRights_directFinalHandover_Projektleitung2SuW_request: 'Fehlende Rechte für Anfrage',
    missingRights_directFinalHandover_Projektleitung2SuW_approved: 'Fehlende Rechte zum annehmen',
    missingRights_directFinalHandover_Projektleitung2SuW_rejected: 'Fehlende Rechte zum ablehnen',
    missingRights_preliminaryHandover_Projektleitung2SuW_request: 'Fehlende Rechte für Anfrage',
    missingRights_preliminaryHandover_Projektleitung2SuW_approved: 'Fehlende Rechte zum annehmen',
    missingRights_preliminaryHandover_Projektleitung2SuW_rejected: 'Fehlende Rechte zum ablehnen',
    missingRights_preliminaryToFinalHandover_Projektleitung2SuW_request: 'Fehlende Rechte für Anfrage',
    missingRights_preliminaryToFinalHandover_Projektleitung2SuW_approved: 'Fehlende Rechte zum annehmen',
    missingRights_preliminaryToFinalHandover_Projektleitung2SuW_rejected: 'Fehlende Rechte zum ablehnen',
    missingRights_finalHandover_Projektleitung2SuW_request: 'Fehlende Rechte für Anfrage',
    missingRights_finalHandover_Projektleitung2SuW_approved: 'Fehlende Rechte zum annehmen',
    missingRights_finalHandover_Projektleitung2SuW_rejected: 'Fehlende Rechte zum ablehnen',
    missingRights_SuWBetrieb_request: 'Fehlende Rechte für Anfrage',
    missingRights_SuWBetrieb_approved: 'Fehlende Rechte zum annehmen',
    missingRights_SuWBetrieb_rejected: 'Fehlende Rechte zum ablehnen',
    missingRights_GewaehrleistungsUebergabe_request: 'Fehlende Rechte für Anfrage',
    missingRights_GewaehrleistungsUebergabe_approved: 'Fehlende Rechte zum annehmen',
    missingRights_GewaehrleistungsUebergabe_rejected: 'Fehlende Rechte zum ablehnen',
    missingRights_Fremdbetrieb_request: 'Fehlende Rechte für Anfrage',
    missingRights_Fremdbetrieb_request_old: 'Fehlende Rechte zum Setzen in Fremdbetrieb',
    missingRights_Fremdbetrieb_approved: 'Fehlende Rechte zum annehmen',
    missingRights_Fremdbetrieb_rejected: 'Fehlende Rechte zum ablehnen',

    dropdown_returnTo_Vertrieb2PE: 'Rückgabe zu „Projektinitiierung“',
    dropdown_returnTo_PE2Vertrieb: 'Rückgabe zu „Standortkonzept“',
    dropdown_returnTo_Vertrieb2ENG: 'Rückgabe zu „Vertrieb an ENG“',
    dropdown_returnTo_ENG2Vertrieb: 'Rückgabe zu „Umsetzungsplanung“',
    dropdown_returnTo_CompletionOfPlanning: 'Rückgabe zu „Planungsabschluss“',
    dropdown_returnTo_Vertrieb2Projektleitung: 'Rückgabe zu „Vertrieb an Projektleitung“',
    dropdown_returnTo_Projektleitung2SuW: 'Rückgabe zu „Projektleitung an Team Betrieb',
    dropdown_returnTo_preliminaryHandover_Projektleitung2SuW: 'Rückgabe zu „Vorläufige Übergabe an Team Betrieb“',
    dropdown_returnTo_SuW2Running: 'Rückgabe zu „Team Betrieb an Running“',

    tabTitle_Vertrieb2PE: 'Projektinitiierung',
    tabTitle_PE2Vertrieb: 'Standortkonzept',
    tabTitle_Vertrieb2ENG: 'Vertrieb an ENG',
    tabTitle_ENG2Vertrieb: 'Umsetzungsplanung',
    tabTitle_CompletionOfPlanning: 'Planungsabschluss',
    tabTitle_CompletionOfPlanning2Vertrieb: 'Planungsabschluss',
    tabTitle_Vertrieb2Projektleitung: 'Vertrieb an Projektleitung',
    tabTitle_Projektleitung2SuW: 'Projektleitung an Team Betrieb',
    tabTitle_SuW2Running: 'abgeschlossen: In Betrieb',
    tabTitle_SuW2Running_warranty: 'Gewährleistungsübergabe',
    tabTitle_preliminaryHandover_Projektleitung2SuW: 'Vorläufige Übergabe an Team Betrieb',
    tabTitle_finalHandover_Projektleitung2SuW: 'Finale Übergabe an Team Betrieb',

    uebergabeTitle_Vertrieb2PE: 'Projektinitiierung',
    uebergabeTitle_PE2Vertrieb: 'Standortkonzept',
    uebergabeTitle_Vertrieb2ENG: 'Vertrieb an ENG',
    uebergabeTitle_ENG2Vertrieb: 'Umsetzungsplanung',
    uebergabeTitle_CompletionOfPlanning: 'Planungsabschluss',
    uebergabeTitle_CompletionOfPlanning2Vertrieb: 'Planungsabschluss',
    uebergabeTitle_Vertrieb2Projektleitung: 'Vertrieb an Projektleitung',
    uebergabeTitle_Projektleitung2SuW: 'Projektleitung an Team Betrieb',
    uebergabeTitle_SuW2Running: 'abgeschlossen: In Betrieb',
    uebergabeTitle_SuW2Running_warranty: 'Gewährleistungsübergabe',
    uebergabeTitle_preliminaryHandover_Projektleitung2SuW: 'Vorläufige Übergabe an Team Betrieb',
    uebergabeTitle_finalHandover_Projektleitung2SuW: 'Finale Übergabe an Team Betrieb',

    returnFrom_SuW_TO_Vertrieb2PE: 'Rückgabe zur Projektinitiierung',
    returnFrom_SuW_TO_PE2Vertrieb: 'Rückgabe zu Standortkonzept',
    returnFrom_SuW_TO_Vertrieb2ENG: 'Rückgabe zu Vertrieb an ENG',
    returnFrom_SuW_TO_ENG2Vertrieb: 'Rückgabe zur Umsetzungsplanung',
    returnFrom_SuW_TO_Vertrieb2Projektleitung: 'Rückgabe zu Vertrieb an Projektleitung',
    returnFrom_SuW_TO_Projektleitung2SuW: 'Rückgabe zu Projektleitung an Team Betrieb',
    returnFrom_Projektleitung2SuW_TO_Vertrieb2PE: 'Rückgabe zur Projektinitiierung',
    returnFrom_Projektleitung2SuW_TO_PE2Vertrieb: 'Rückgabe zu Standortkonzept',
    returnFrom_Projektleitung2SuW_TO_Vertrieb2ENG: 'Rückgabe zu Vertrieb an ENG',
    returnFrom_Projektleitung2SuW_TO_ENG2Vertrieb: 'Rückgabe zur Umsetzungsplanung',
    returnFrom_Projektleitung2SuW_TO_Vertrieb2Projektleitung: 'Rückgabe zu Vertrieb an Projektleitung',
    returnFrom_CompletionOfPlanning_TO_ENG2Vertrieb: 'Rückgabe zur Umsetzungsplanung',
    returnFrom_Vertrieb2Projektleitung_TO_CompletionOfPlanning: 'Rückgabe zum Planungsabschluss',
    returnFrom_Vertrieb2Projektleitung_TO_Vertrieb2PE: 'Rückgabe zur Projektinitiierung',
    returnFrom_Vertrieb2Projektleitung_TO_PE2Vertrieb: 'Rückgabe zu Standortkonzept',
    returnFrom_Vertrieb2Projektleitung_TO_Vertrieb2ENG: 'Rückgabe zu Vertrieb an ENG',
    returnFrom_Vertrieb2Projektleitung_TO_ENG2Vertrieb: 'Rückgabe zur Umsetzungsplanung',
    returnFrom_ENG2Vertrieb_TO_Vertrieb2PE: 'Rückgabe zur Projektinitiierung',
    returnFrom_ENG2Vertrieb_TO_PE2Vertrieb: 'Rückgabe zu Standortkonzept',
    returnFrom_ENG2Vertrieb_TO_Vertrieb2ENG: 'Rückgabe zu Vertrieb an ENG',
    returnFrom_Vertrieb2ENG_TO_Vertrieb2PE: 'Rückgabe zur Projektinitiierung',
    returnFrom_Vertrieb2ENG_TO_PE2Vertrieb: 'Rückgabe zu Standortkonzept',
    returnFrom_PE2Vertrieb_TO_Vertrieb2PE: 'Rückgabe zur Projektinitiierung',
    returnFrom_GewaehrleistungsUebergabe_TO_Projektleitung2SuW: 'Rückgabe zu Vertrieb an Projektleitung',
    returnFrom_preliminaryHandover_Projektleitung2SuW_TO_Projektleitung2SuW: 'Rückgabe zu Vertrieb an Projektleitung',
    returnFrom_directFinalHandover_Projektleitung2SuW_TO_Projektleitung2SuW: 'Rückgabe zu Projektleitung an Team Betrieb',
    returnFrom_preliminaryToFinalHandover_Projektleitung2SuW_TO_Projektleitung2SuW: 'Rückgabe zu Projektleitung an Team Betrieb',
    returnFrom_finalHandover_Projektleitung2SuW_TO_Projektleitung2SuW: 'Rückgabe zu Projektleitung an Team Betrieb',
    returnFrom_preliminaryToFinalHandover_Projektleitung2SuW_TO_preliminaryHandover_Projektleitung2SuW: 'Rückgabe zur vorläufigen Übergabe an Team Betrieb',
    returnFrom_finalHandover_Projektleitung2SuW_TO_preliminaryHandover_Projektleitung2SuW: 'Rückgabe zur vorläufigen Übergabe an Team Betrieb',

    uebergabeAction_request: "Übergabe angefragt",
    uebergabeAction_preRequest: "Überprüfung",
    uebergabeAction_preRequestRejected: "Überprüfung abgelehnt",
    uebergabeAction_approved: "Übergabe angenommen",
    uebergabeAction_rejected: "Übergabe abgelehnt",
    uebergabeAction_cancelled: "Abgebrochen",
    uebergabeAction_returnFrom: "Zurückgegeben",
  }
}

export type TranslationMap = ReturnType<typeof BuildTranslationMap>
